// tslint:disable:ordered-imports
import { Component, OnDestroy } from "@angular/core";
import { TranslationType } from "../../../models/translation-type";
import { ActivatedRoute, Params, Router } from "@angular/router";
import { TranslationService } from "../../../services/translation.service";
import { User } from "../../../models/user";
import { Store } from "@ngrx/store";
import { ConfirmComponent } from "../../../modals/confirm/confirm.component";
import { MdDialog } from "@angular/material";
import { Observable } from "rxjs/Observable";
import { SuccessComponent } from "../../../modals/success/success.component";
import { Subscription } from "rxjs/Subscription";
import { Translation } from "../../../models/translation";
import { Language } from "../../../models/language";
import { TranslationsActions } from "../../../store/actions/translations.actions";
import { AdminService } from "../../../services/admin.service";
import { LoadingComponent } from "../../../modals/loading/loading.component";
import * as cyrillicToLatin from "cyrillic-to-latin";
import {
  ITranslationTypes,
  TranslationTypesActions,
} from "../../../store/actions/translation-types.actions";

@Component({
  selector: "app-translations-list",
  templateUrl: "./translations-list.component.html",
  styleUrls: ["./translations-list.component.scss"],
})
export class TranslationsListComponent implements OnDestroy {
  translations: Translation[];
  editing = [];
  total = 0;
  loadingIndicator: boolean;
  loading: boolean;
  term: string;
  keywords: string;
  subscriptions: Subscription[];
  translationType: TranslationType;
  editMode: boolean;
  formats: any[] = ["16:9", "4:3", "3:4"];

  /**
   * API parameters
   */
  paramId: number;
  currentPage: number;
  orderByDescending: string;
  orderOn: number;
  language: Language;
  translated: boolean;

  /**
   * Initialize variables
   * @param {ActivatedRoute} activatedRoute
   * @param {TranslationService} translationService
   * @param {Store<any>} store
   * @param {Router} router
   * @param {AdminService} adminService
   * @param {TranslationsActions} translationsActions
   * @param {MdDialog} dialog
   */
  constructor(
    private activatedRoute: ActivatedRoute,
    private translationService: TranslationService,
    private translationTypesActions: TranslationTypesActions,
    private store: Store<any>,
    private router: Router,
    private adminService: AdminService,
    private translationsActions: TranslationsActions,
    private dialog: MdDialog
  ) {
    this.subscriptions = [];

    this.translations = [];
    /**
     * Check user rights for this component
     */
    this.adminService.init().subscribe(() => {
      this.editMode = this.adminService.can("Translate", "Edit");
    });

    /**
     * Get current user, translations types and translations from the store and initialize the translations
     */
    this.subscriptions.push(
      this.store.select("userState").subscribe((user: User) => {
        if (user && user.currentLanguage) {
          this.translationService
            .getTranslationTypes(user.currentLanguage)
            .subscribe((types: TranslationType[]) => {
              this.translationTypesActions.setTranslationTypes(types);
            });
          this.language = user.currentLanguage;
          this.activatedRoute.params.subscribe((params: Params) => {
            this.translated =
              params.translated && params.translated !== "null"
                ? params.translated === "Translated"
                : null;
            if (params.searchTerm) {
              this.term = params.searchTerm;
            }
            if (params.id) {
              this.loading = true;
              this.paramId = params.id;
              this.translationsActions.setTranslations([]);
              this.initTranslations();
              this.subscriptions.push(
                this.store
                  .select("translationTypesState")
                  .subscribe((translations: ITranslationTypes) => {
                    if (translations && translations.translationTypes) {
                      this.translationType = translations.translationTypes.find(
                        (translationType) => translationType.id === params.id
                      );
                    }
                  })
              );
            }
          });

          this.subscriptions.push(
            this.store
              .select("translationsState")
              .subscribe((translations: Translation[]) => {
                this.translations = translations;
              })
          );
        }
      })
    );
  }

  /**
   * Set parameters to fetch the translations
   */
  initParams() {
    this.translations = [];
    this.currentPage = 1;
    this.orderByDescending = "false";
    this.orderOn = 1;
    this.keywords = "";
  }

  /**
   * Initialize the parameters and load the translations
   */
  initTranslations() {
    this.initParams();
    this.loadTranslations();
  }

  /**
   * Fetch the translations with parameters
   */
  loadTranslations() {
    this.loadingIndicator = true;
    this.translationService
      .getTranslationsByType(
        this.paramId,
        this.language.id,
        (this.currentPage - 1) * 50,
        50,
        this.orderByDescending,
        this.orderOn,
        this.term,
        this.translated
      )
      .subscribe((data) => {
        this.translationsActions.setTranslations(
          this.translations.concat(data.translations)
        );
        this.total = data.translations.length;
        this.loadingIndicator = false;
        this.loading = false;
      });
  }

  /**
   * When the focus is lost on a translation, stop the editing mode and update the value
   * @param event
   * @param cell
   * @param rowIndex
   */
  onBlur(event, cell, rowIndex) {
    this.updateValue(event.target.value, cell, rowIndex);
    this.updateRowIfEmpty(event.target.value, cell, rowIndex);
  }

  /**
   * Update the translation value with the input value submitted
   * @param value
   * @param cell
   * @param rowIndex
   */
  updateValue(value, cell, rowIndex) {
    if (this.translations[rowIndex][cell].value !== value && value.length > 0) {
      this.translations[rowIndex][cell].value =
        this.language.isoCode === "sr" ? cyrillicToLatin(value) : value;
      this.translations[rowIndex][cell].modified = true;
      this.translations[rowIndex].toUpdate = true;
    }
    this.editing[rowIndex + "-" + cell] = false;
  }

  /**
   * Get value from the translations array
   * @param cell
   * @param rowIndex
   * @returns {null}
   */
  getValue(cell, rowIndex) {
    if (
      this.translations[rowIndex][cell].value &&
      this.translations[rowIndex][cell].value.length > 0
    ) {
      return this.translations[rowIndex][cell].value;
    } else {
      return null;
    }
  }

  /**
   * Set the edit mode on a row
   * @param rowIndex
   * @param cell
   */
  editRow(rowIndex, cell) {
    if (this.editMode) {
      this.editing = [];
      this.editing[rowIndex + "-" + cell] = true;
    }
  }

  /**
   * Reset the value of a translation
   * @param rowIndex
   * @param cell
   * @param $event
   */
  resetValue(rowIndex, cell, $event) {
    $event.stopPropagation();
    this.translations[rowIndex][cell].value =
      this.translations[rowIndex][cell].initialValue;
    this.translations[rowIndex][cell].modified = false;
    this.editing[rowIndex + "-" + cell] = false;
    if (
      !this.translations[rowIndex].shortName.modified &&
      !this.translations[rowIndex].longName.modified
    ) {
      this.translations[rowIndex].toUpdate = false;
    }
  }

  /**
   * Fetch the translations with a new sorting parameters
   * @param event
   */
  onSort(event) {
    const direction = event.sorts[0].dir;
    this.orderByDescending = direction === "asc" ? "true" : "false";
    this.orderOn = this.getColumnId(event.sorts[0].prop);
    this.translations = [];
    this.currentPage = 1;
    this.loadTranslations();
  }

  /**
   * Get the column id for sort purpose
   * @param {string} propName
   * @returns {number}
   */
  getColumnId(propName: string) {
    const key = propName.split(".").shift();
    if (key === "id") {
      return 0;
    }
    if (this.translations.length > 0) {
      return this.translations[0][key].orderId;
    }
  }

  /**
   * Detect when the user press enter to validate a translation modification and update the translation with the new value
   * @param event
   * @param cell
   * @param rowIndex
   */
  onKeyUp(event, cell, rowIndex) {
    if (event.key === "Enter") {
      this.updateValue(event.target.value, cell, rowIndex);
      this.updateRowIfEmpty(event.target.value, cell, rowIndex);
    }
  }

  /**
   * If the row is empty, update the other value with the first one
   * @param value
   * @param cell
   * @param rowIndex
   */
  updateRowIfEmpty(value, cell, rowIndex) {
    let reverseCell = "shortName";
    if (cell === "shortName") {
      reverseCell = "longName";
    }
    if (!this.getValue(reverseCell, rowIndex)) {
      this.updateValue(value, reverseCell, rowIndex);
    }
  }

  /**
   * Set a specific css class on row which are not saved yet
   * @param row
   * @returns {string}
   */
  // noinspection JSMethodCanBeStatic
  getRowClass(row) {
    if (row && row.toUpdate) {
      return "to-update";
    }
  }

  /**
   * Count the translations modified but not saved
   * @returns {number}
   */
  getNumberOfTranslationModified() {
    return this.translations.filter(
      (translation) => translation.toUpdate === true
    ).length;
  }

  /**
   * Fetch the translation for a keyword
   */
  searchTerm() {
    this.keywords = this.term;
    this.translations = [];
    this.currentPage = 1;
    this.loading = true;
    this.loadTranslations();
  }

  /**
   * Clear the keyword term and fetch the translations
   */
  searchClear() {
    this.term = "";
    this.keywords = this.term;
    this.translations = [];
    this.currentPage = 1;
    this.loading = true;
    this.loadTranslations();
  }

  /**
   * Save the translations with an API call
   */
  saveTranslations() {
    const loadingRef = this.dialog.open(LoadingComponent);
    const translations = this.translations.filter(
      (translation) => translation.toUpdate === true
    );
    this.translationService.saveTranslations(translations).subscribe((res) => {
      loadingRef.close();
      loadingRef.afterClosed().subscribe(() => {
        if (res.success === 1) {
          this.dialog.open(SuccessComponent, {
            data: { message: "Translations has been saved" },
          });
          this.translations.map((translation) => translation.setSavedStatus());
        } else {
          this.dialog.open(SuccessComponent, {
            data: { type: "warning", message: res.message },
          });
        }
      });
    });
  }

  /**
   * Open a confirmation modal if the user want to leave with translations not saved
   * @returns {Observable<boolean>}
   */
  formSavedCheck(): Observable<boolean> {
    const title = "Translations not saved";
    const message =
      "You have translation pending to be saved, leave this page anyway ?";
    return this.dialog
      .open(ConfirmComponent, { data: { title, message } })
      .afterClosed();
  }

  /**
   * On destroy event, unsubscribe all the subscriptions
   */
  ngOnDestroy() {
    this.subscriptions.forEach((subscription) => {
      subscription.unsubscribe();
    });
  }

  /**
   * Translate a value with google translate
   * @param {string} toTranslate
   * @param rowIndex
   */
  googleTranslate(toTranslate: string, rowIndex) {
    if (this.language.isoCode === "en") {
      this.updateValue(this.capitalize(toTranslate), "shortName", rowIndex);
      this.updateValue(this.capitalize(toTranslate), "longName", rowIndex);
    } else {
      this.translationService
        .getGoogleTranslation(toTranslate, this.language.isoCode, "en")
        .subscribe((res: any) => {
          this.updateValue(
            this.capitalize(
              res.data.translations[0].translatedText.substring(0, 25)
            ),
            "shortName",
            rowIndex
          );
          this.updateValue(
            this.capitalize(
              res.data.translations[0].translatedText.substring(0, 100)
            ),
            "longName",
            rowIndex
          );
        });
    }
  }

  googleTranslateAll() {
    if (this.language.isoCode === "en") {
      this.translations
        .filter(
          (t) => t.longName.value === null || t.longName.value === undefined
        )
        .forEach((element) => {
          let index = this.translations.findIndex(
            (t) => t.elementExternalId === element.elementExternalId
          );
          this.updateValue(
            this.capitalize(element.name.value),
            "longName",
            index
          );
        });

      this.translations
        .filter(
          (t) => t.shortName.value === null || t.shortName.value === undefined
        )
        .forEach((element) => {
          let index = this.translations.findIndex(
            (t) => t.elementExternalId === element.elementExternalId
          );
          this.updateValue(
            this.capitalize(element.name.value),
            "shortName",
            index
          );
        });
    } else {
      this.translations
        .filter(
          (t) => t.longName.value === null || t.longName.value === undefined
        )
        .forEach((element) => {
          this.translationService
            .getGoogleTranslation(
              element.name.value,
              this.language.isoCode,
              "en"
            )
            .subscribe((res: any) => {
              let index = this.translations.findIndex(
                (t) => t.elementExternalId === element.elementExternalId
              );
              this.updateValue(
                this.capitalize(
                  res.data.translations[0].translatedText.substring(0, 100)
                ),
                "longName",
                index
              );
            });
        });

      this.translations
        .filter(
          (t) => t.shortName.value === null || t.shortName.value === undefined
        )
        .forEach((element) => {
          this.translationService
            .getGoogleTranslation(
              element.name.value,
              this.language.isoCode,
              "en"
            )
            .subscribe((res: any) => {
              let index = this.translations.findIndex(
                (t) => t.elementExternalId === element.elementExternalId
              );
              this.updateValue(
                this.capitalize(
                  res.data.translations[0].translatedText.substring(0, 25)
                ),
                "shortName",
                index
              );
            });
        });
    }
  }

  /**
   * Open the edit picture modal
   * @param {Translation} translation
   * @param {string} format
   */
  editPicture(translation: Translation, format: string) {
    if (format === "Pictogram") {
      this.router.navigate(
        [
          {
            outlets: {
              modal: [
                "edit",
                "pictogram",
                translation.elementExternalId,
                this.translationType.id,
                format,
                "translate",
              ],
            },
          },
        ]
      );
    } else {
      this.router.navigate(
        [
          {
            outlets: {
              modal: [
                "edit",
                "picture",
                translation.elementExternalId,
                this.translationType.id,
                format,
                "translate",
              ],
            },
          },
        ],
        {
          queryParams: {
            format: this.formats,
          },
        }
      );
    }
  }

  /**
   * Set the translated (or not) parameter and fetch translations
   * @param value
   */
  setTranslated(value) {
    this.loading = true;
    this.initParams();
    this.translated = value;
    this.loadTranslations();
  }

  getTooltip(translation: Translation) {
    if (translation.tooltipContent === "Loading...") {
      this.translationService
        .getTooltip(this.paramId, translation.elementExternalId)
        .subscribe((data) => {
          translation.tooltipContent = data;
        });
    }
  }

  seeClassifications(translation: Translation) {
    let key = "";
    if (
      translation.name &&
      translation.name.value &&
      translation.name.value.trim() !== ""
    ) {
      key = translation.name.value;
    } else if (
      translation.shortName &&
      translation.shortName.value &&
      translation.shortName.value.trim() !== ""
    ) {
      key = translation.shortName.value;
    } else if (
      translation.longName &&
      translation.longName.value &&
      translation.longName.value.trim() !== ""
    ) {
      key = translation.longName.value;
    }
    if (translation) {
      this.router.navigate([
        {
          outlets: {
            modal: [
              "view",
              "translate-classification",
              translation.elementExternalId,
              key,
            ],
          },
        },
      ]);
    }
  }

  // transform google translation
  capitalize(value: string) {
    if (!value) return "";

    value = value.toLowerCase();
    return value.charAt(0).toUpperCase() + value.slice(1);
  }
}



// WEBPACK FOOTER //
// ./src/app/pages/translate/translations-list/translations-list.component.ts